import BaseModel from '@core/model';

import AdditionalNeedsTypeEnum from '@constants/enums/additionalNeedsType';

import i18n from '@plugins/i18n';

import { Investor } from '@/models';

export class AdditionalNeed extends BaseModel {
    static entity = 'additionalNeed';
    static cacheOriginal = true;

    static fields() {
        return {
            ...super.fields(),
            id: this.string(null).nullable(),
            type: this.enum(AdditionalNeedsTypeEnum).nullable(),
            expires_at: this.string(null).nullable(),
            notes: this.string(null).nullable(),

            investor_id: this.string(null).nullable(),
            investor: this.belongsTo(Investor, 'investor_id')
        };
    }

    get name() {
        return i18n.t(`enums.additional_needs_type.${this.type}`);
    }

    get description() {
        return i18n.t(`enums.additional_needs_type_description.${this.type}`);
    }

    get is_permanent() {
        switch (this.type) {
            case AdditionalNeedsTypeEnum.HEARING:
            case AdditionalNeedsTypeEnum.VISUAL:
            case AdditionalNeedsTypeEnum.COGNITIVE:
            case AdditionalNeedsTypeEnum.LITERACY_NUMERACY:
            case AdditionalNeedsTypeEnum.NEURODIVERSE:
            case AdditionalNeedsTypeEnum.PHYSICAL_LONG_TERM_ILLNESS:
            case AdditionalNeedsTypeEnum.TERMINAL_ILLNESS:
            case AdditionalNeedsTypeEnum.MENTAL_HEALTH:
            case AdditionalNeedsTypeEnum.BLOCKED_ACCESS:
            case AdditionalNeedsTypeEnum.TECHNOLOGY_ISSUES:
                return true;
            case AdditionalNeedsTypeEnum.EMOTIONAL_LIFE_CHANGE:
            case AdditionalNeedsTypeEnum.FINANCIAL_HARDSHIP:
            case AdditionalNeedsTypeEnum.TEMPORARY_ILL_HEALTH:
            case AdditionalNeedsTypeEnum.UNEMPLOYMENT_REDUNDANCY:
                return false;
            default:
                return false;
        }
    }

    get is_temporary() {
        return !this.is_permanent;
    }

    get is_enabled() {
        return this.id !== null;
    }

    getEmpty() {
        return new AdditionalNeed({
            id: null,
            type: this.type,
            expires_at: null,
            notes: null,
            investor_id: this.investor_id,
            investor: this.investor
        });
    }

    async $enable() {
        return await Investor.$enableAdditionalNeed(this.investor_id, this.$toPayload());
    }

    async $disable() {
        const disabledNeed = this.getEmpty();

        if (!this.id) {
            return disabledNeed;
        }

        await Investor.$disableAdditionalNeed(this.investor_id, this.id);

        return disabledNeed;
    }

    async $updateNotes(notes = null) {
        this.$saving = ['notes'];

        let response = null;

        try {
            response = await Investor.$updateAdditionalNeed(this.investor_id, this.id, { notes, type: this.type });
        } finally {
            this.$saving = this.$saving.filter(saving => saving !== 'notes');
        }

        return response;
    }
}

export default AdditionalNeed;
